/**
 * Adaptive coaching API schemas (T12.5 / ISSUE-072 / ADR-0026).
 * Browser-safe — no @nutians/db imports.
 */
import { z } from 'zod';
import { ADAPT_MIN_HISTORY_DAYS, ADAPT_MIN_LOGGED_DAYS, ADAPT_MIN_WEIGH_INS, } from '../coaching/constants.js';
export const coachingStatusSchema = z.enum(['calibrating', 'ready']);
/** Neutral progress toward enough signal for adaptive coaching. */
export const coachingReadinessSchema = z.object({
    historyDays: z.number().int().nonnegative(),
    requiredHistoryDays: z.literal(ADAPT_MIN_HISTORY_DAYS),
    loggedDays: z.number().int().nonnegative(),
    requiredLoggedDays: z.literal(ADAPT_MIN_LOGGED_DAYS),
    weighInDays: z.number().int().nonnegative(),
    requiredWeighInDays: z.literal(ADAPT_MIN_WEIGH_INS),
    status: coachingStatusSchema,
});
/** A persisted expenditure estimate row (append-only audit). */
export const expenditureEstimateViewSchema = z.object({
    id: z.string().uuid(),
    computedAt: z.string().datetime(),
    windowStart: z.string().date(),
    windowEnd: z.string().date(),
    // Expenditure numbers — omitted for free-tier teasers.
    eFinalKcal: z.number().optional(),
    eFormulaKcal: z.number().optional(),
    confidence: z.number().optional(),
    sufficient: z.boolean(),
    status: coachingStatusSchema,
    // Detailed fields — omitted for free-tier teasers
    meanIntakeKcal: z.number().optional(),
    deltaTrendKg: z.number().optional(),
    eBalanceKcal: z.number().optional(),
});
/** A proposed target (preview; not stored until the user accepts). */
export const coachingTargetProposalSchema = z.object({
    kcal: z.number().int(),
    proteinG: z.number().int(),
    carbsG: z.number().int(),
    fatG: z.number().int(),
    fiberG: z.number().int(),
    cappedBySafety: z.boolean(),
    notes: z.array(z.string()),
    effectiveFrom: z.string().date(),
    explainKey: z.string(),
    explainParams: z.record(z.string(), z.unknown()),
    /**
     * True when the late-luteal phase-normalization rule suppressed a calorie cut
     * (ADR-0026 rev. / ADR-0030 §3 / T19.4). Clients may use this to badge the
     * explanation as cycle-aware. Only present when a hold actually fired.
     */
    phaseHeld: z.boolean().optional(),
});
/**
 * Non-binding "active day" hint derived from imported steps (ADR-0027 / T14.3).
 * CONTEXT ONLY — never alters energy balance, expenditure, or macro split.
 * The macro split is always driven by the user's explicit schedule (ADR-0026 §3).
 */
export const activityDayHintSchema = z.object({
    date: z.string().date(),
    steps: z.number().int().nonnegative(),
    /** True when steps >= 7_500 (common "active day" threshold). Display hint only. */
    isActive: z.boolean(),
});
/** GET /me/coaching/expenditure response. */
export const coachingExpenditureResponseSchema = z.object({
    status: coachingStatusSchema,
    readiness: coachingReadinessSchema,
    estimate: expenditureEstimateViewSchema.nullable(),
    /** True when the response is a free-tier teaser (numbers hidden). */
    teaser: z.boolean().default(false),
    /**
     * Non-binding step hints for days in the estimate window that have imported
     * activity data. UI may surface these as "active / rest day" indicators.
     * Steps NEVER alter the energy balance or macro split (ADR-0026 §3 / ADR-0027 §2).
     */
    activityDays: z.array(activityDayHintSchema).optional(),
});
/** POST /me/coaching/review body. */
export const coachingReviewBodySchema = z.object({
    action: z.enum(['preview', 'accept', 'keep']),
    /** Custom kcal override before accept (optional). Must be within safety bounds. */
    customKcal: z.number().int().positive().optional(),
});
/** POST /me/coaching/review response. */
export const coachingReviewResponseSchema = z.object({
    status: coachingStatusSchema,
    estimate: expenditureEstimateViewSchema.nullable(),
    proposal: coachingTargetProposalSchema.nullable(),
    /** When action=accept: the id of the newly created target row. */
    targetId: z.string().uuid().optional(),
    /**
     * Non-binding step hints for days in the estimate window (same as expenditure response).
     * CONTEXT ONLY — never alters energy balance, expenditure, or macro split.
     */
    activityDays: z.array(activityDayHintSchema).optional(),
});
/** A single item in GET /me/coaching/history. */
export const coachingHistoryItemSchema = z.object({
    estimate: expenditureEstimateViewSchema,
    /** The target row applied from this estimate, if the user accepted. */
    targetApplied: z
        .object({
        id: z.string().uuid(),
        kcal: z.number().int(),
        effectiveFrom: z.string().date(),
        cappedBySafety: z.boolean(),
    })
        .nullable(),
});
/** GET /me/coaching/history response. */
export const coachingHistoryResponseSchema = z.object({
    items: z.array(coachingHistoryItemSchema),
});
//# sourceMappingURL=coaching.js.map